import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import {
  ToggleRadioButtonGroup,
  type ToggleRadioButtonGroupProps,
} from '~/components/elements/ToggleRadioButtonGroup'
import { useLogEvent } from '~/hooks/useLogEvent'
import {
  compact,
  type DataDisplayType,
  dataDisplayTypeLabelI18nKeyMap,
  dataDisplayTypes,
} from '~/utils'

type Attribute =
  ToggleRadioButtonGroupProps<DataDisplayType>['attributes'][number]

const logEventNameMap: Record<DataDisplayType, string> = {
  percentage: 'Show %',
  number: 'Show sample size',
  estimatedPopulation: 'Show estimated population',
}

export type DataDisplayTypeSwitcherProps = {
  'data-testid'?: string
  disabled?: boolean
  onChange?: (value: DataDisplayType) => void
  value: DataDisplayType
  withoutNumber?: boolean
}

export const DataDisplayTypeSwitcher = ({
  'data-testid': dataTestId = 'components-features-data-display-type-switcher',
  disabled,
  onChange,
  value,
  withoutNumber = false,
}: DataDisplayTypeSwitcherProps) => {
  const { t } = useTranslation()

  const attributeMap: Record<DataDisplayType, Attribute> = React.useMemo(
    () =>
      R.mapToObj(dataDisplayTypes, (dataDisplayType) => [
        dataDisplayType,
        {
          text: t(dataDisplayTypeLabelI18nKeyMap[dataDisplayType]),
          value: dataDisplayType,
        },
      ]),
    [t],
  )

  const attributes = React.useMemo(
    () =>
      compact([
        attributeMap.percentage,
        !withoutNumber && attributeMap.number,
        attributeMap.estimatedPopulation,
      ]),
    [attributeMap, withoutNumber],
  )

  const logEvent = useLogEvent()
  const handleChange = React.useCallback<
    EH<ToggleRadioButtonGroupProps<DataDisplayType>, 'onChange'>
  >(
    (dataDisplayType) => {
      onChange?.(dataDisplayType)
      logEvent(logEventNameMap[dataDisplayType], { withSuffix: true })
    },
    [logEvent, onChange],
  )

  return (
    <ToggleRadioButtonGroup
      attributes={attributes}
      data-testid={dataTestId}
      disabled={disabled}
      value={value}
      onChange={handleChange}
    />
  )
}
